<template>
    <!-- List of members BO granted (or revoked) Affiliate eligibility, opened
         from การตลาด → Affiliate: สมาชิก. Granting happens on the member page
         (จัดการสมาชิก → tab Affiliate), where staff already find the customer. -->
    <el-dialog :visible="visible" width="860px" append-to-body :title="$t('marketing.affiliate.eligibility.list_title')"
               @update:visible="(v) => $emit('update:visible', v)" @open="reload(1)">
        <div data-testid="aff-elig-dialog">
            <el-alert v-if="schemaMissing" type="error" :closable="false" show-icon class="mb-2" :title="$t('marketing.affiliate.eligibility.schema_missing')"></el-alert>
            <p class="small text-muted mb-2">{{ $t("marketing.affiliate.eligibility.list_hint") }}</p>
            <div class="d-flex flex-wrap align-items-center mb-2" style="gap: 12px;">
                <el-radio-group v-model="f.eligible" size="small" data-testid="aff-elig-filter" @change="reload(1)">
                    <el-radio-button v-for="k in filters" :key="k" :label="k">{{ tk("marketing.affiliate.eligibility.filter_", k) }}</el-radio-button>
                </el-radio-group>
                <el-input v-model="f.q" size="small" clearable prefix-icon="el-icon-search" :placeholder="$t('marketing.affiliate.eligibility.search')" style="max-width: 260px;" data-testid="aff-elig-search" @input="onSearch"></el-input>
            </div>
            <el-table v-loading="loading" :data="rows" size="small" class="aff-table bon-table bon-tight" :empty-text="$t('marketing.affiliate.empty')" data-testid="aff-elig-table">
                <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.member.name') + ' / ' + $t('marketing.affiliate.member.phone')" min-width="170">
                    <template v-slot="{ row }">
                        <router-link :to="'/members/' + row.member_id" @click.native="$emit('update:visible', false)">{{ row.member_deleted ? $t("marketing.affiliate.member.deleted_name") : row.name }}</router-link>
                        <span class="small text-muted"> #{{ row.member_id }}</span>
                        <div class="small text-muted">{{ row.phone_masked || "-" }}</div>
                    </template>
                </el-table-column>
                <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.eligibility.status')" width="110">
                    <template v-slot="{ row }"><el-tag size="small" :type="tagOf(row)">{{ tk("marketing.affiliate.eligibility.status_", keyOf(row)) }}</el-tag></template>
                </el-table-column>
                <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.member.code')" width="110">
                    <template v-slot="{ row }">{{ row.affiliate ? row.affiliate.code : $t("marketing.affiliate.eligibility.not_joined") }}</template>
                </el-table-column>
                <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.eligibility.changed')" min-width="170">
                    <template v-slot="{ row }">
                        <div>{{ fmt(row.eligible ? row.granted_at : row.revoked_at) }}</div>
                        <div class="small text-muted">{{ row.eligible ? row.granted_by_name : row.revoked_by_name }}<span v-if="row.note"> · {{ row.note }}</span></div>
                    </template>
                </el-table-column>
                <el-table-column v-if="canWrite" class-name="bon-tight" :label="$t('marketing.affiliate.actions')" width="110" align="center">
                    <template v-slot="{ row }">
                        <base-button v-if="row.eligible" size="sm" type="danger" :loading="actingId === row.member_id" data-testid="aff-elig-row-revoke" @click="revoke(row)">{{ $t("marketing.affiliate.eligibility.revoke") }}</base-button>
                        <base-button v-else-if="!row.member_deleted" size="sm" type="success" :loading="actingId === row.member_id" @click="grant(row)">{{ $t("marketing.affiliate.eligibility.grant") }}</base-button>
                    </template>
                </el-table-column>
            </el-table>
            <el-pagination class="mt-2 text-right" layout="total, prev, pager, next" :total="total" :page-size="limit" :current-page="f.page" @current-change="reload"></el-pagination>
        </div>
    </el-dialog>
</template>

<script>
import { Dialog, Alert, RadioGroup, RadioButton, Input, Table, TableColumn, Tag, Pagination, Loading } from "element-ui";
import { mapGetters } from "vuex";
import { AffiliateService } from "@/resource";
import { isSchemaMissing } from "@/utils/marketing";
import { LIST_LIMIT, ELIGIBILITY_FILTERS, eligibilityKey, eligibilityTag, eligibilityQuery } from "@/utils/affiliate";
import affiliateMixin from "./affiliateMixin";

export default {
    name: "AffiliateEligibilityDialog",
    directives: { loading: Loading.directive },
    components: {
        [Dialog.name]: Dialog,
        [Alert.name]: Alert,
        [RadioGroup.name]: RadioGroup,
        [RadioButton.name]: RadioButton,
        [Input.name]: Input,
        [Table.name]: Table,
        [TableColumn.name]: TableColumn,
        [Tag.name]: Tag,
        [Pagination.name]: Pagination
    },
    mixins: [affiliateMixin],
    props: { visible: { type: Boolean, default: false } },
    data() {
        return { loading: false, schemaMissing: false, rows: [], total: 0, limit: LIST_LIMIT, filters: ELIGIBILITY_FILTERS,
            f: { page: 1, eligible: "true", q: "" }, searchTimer: null, actingId: null };
    },
    computed: {
        ...mapGetters({ canWrite: "Auth/canWriteMarketing" })
    },
    methods: {
        keyOf: eligibilityKey,
        tagOf(row) {
            return eligibilityTag(eligibilityKey(row));
        },
        async reload(page) {
            if (page) this.f.page = page;
            this.loading = true;
            try {
                const body = (await AffiliateService.eligibilityList(eligibilityQuery(this.f, this.limit))).data || {};
                this.rows = body.data || [];
                this.total = body.total || 0;
                this.schemaMissing = false;
            } catch (err) {
                this.rows = [];
                this.total = 0;
                if (isSchemaMissing(err)) this.schemaMissing = true;
                else this.showError(err);
            } finally {
                this.loading = false;
            }
        },
        onSearch() {
            clearTimeout(this.searchTimer);
            this.searchTimer = setTimeout(() => this.reload(1), 350);
        },
        async setRow(row, grant) {
            const text = this.$t(grant ? "marketing.affiliate.eligibility.confirm_grant" : "marketing.affiliate.eligibility.confirm_revoke");
            if (!(await this.confirmBox(text, { danger: !grant, title: row.name }))) return;
            this.actingId = row.member_id;
            try {
                if (grant) await AffiliateService.grantEligibility(row.member_id, {});
                else await AffiliateService.revokeEligibility(row.member_id, {});
                this.notifyOk(grant ? "marketing.affiliate.eligibility.granted" : "marketing.affiliate.eligibility.revoked");
                this.$emit("changed");
                this.reload();
            } catch (err) {
                this.showError(err);
            } finally {
                this.actingId = null;
            }
        },
        grant(row) {
            return this.setRow(row, true);
        },
        revoke(row) {
            return this.setRow(row, false);
        }
    }
};
</script>
